// Watches the SDK's onConversationUpdated callback and writes a local
// conversations row so peer-initiated chats appear in the chat list
// panel without a manual refresh.
//
// Why this exists: the inviter side runs ensureConversation() from
// NewChatModal at chat-creation time. The receiver never goes through
// that flow — the BE fan-out delivers the Welcome envelope, the SDK
// joins the MLS group, but no code has been writing the corresponding
// local row, so the receiver's chat list stays empty.
//
// The SDK fires onConversationUpdated:
//   - Right after joining a group via a freshly-received Welcome.
//   - After processing each Commit (member add/remove, epoch bump).
//   - After local Commits/sends that mutate the cached meta.
//
// We make the write idempotent (`get` then `put`-if-absent) so:
//   - The inviter's existing row (already inserted via
//     ensureConversation under a different key — the BE UUIDv5) isn't
//     touched.
//   - Subsequent Commits on the same conversation don't clobber the
//     receiver's row.
//
// Conversation id semantics: the receiver doesn't have access to the
// inviter's BE conversationId (that was minted by the inviter calling
// POST /v1/conversations/request and was never persisted on the BE
// side — only the (caller, peer) → UUIDv5 derivation). So we key the
// receiver's local row on the MLS ConversationId hex itself. Each
// side has its own primary key but the UI is independent — both
// surfaces show the chat correctly.
//
// This module was lifted from
// apps/web/src/features/chat/useIncomingConversations.tsx so
// apps/desktop can share the same realtime ingestion path. Storage
// access is injected through `ConversationsPersister` + `KVStore` so
// packages/ui itself doesn't pin to either platform's persistence
// layer.

import { useEffect } from 'react';
import {
  CALL_GROUP_NAME_PREFIX,
  groupConversationPatchFromMeta,
} from '../conversation/groupConversation';
import { isRegisteredCallGroup } from '../calls/callGroupRegistry';
import { isDeviceGroupConversationId } from './deviceGroup';
import { bytesToBase64url } from '../conversation/mlsBytes';
import { resolveMediaUrl } from './mediaUrlResolver';
import { withAvatarCacheKey } from './avatarCacheBust';
import { useSession } from '../messaging/sessionStore';
import { log } from '../log/log';
import { getCurrentUserAccountId } from './registerCurrentUser';
import { useChatSyncStore } from './chatSyncStore';
import type {
  ConversationRecord,
  ConversationsPersister,
  KVStore,
} from './persistenceAdapters';
import type { IncomingNotification } from '../notifications/types';

// Structural slice of the SDK's MessagingClient — see mlsConversation.ts
// for why we don't import the concrete class.
interface ClientWithConversationCallback {
  onConversationUpdated?(
    handler: (id: Uint8Array, epoch: number, sender?: Uint8Array) => void,
  ): () => void;
  listConversations?(): Promise<Array<{ id: Uint8Array; is_device_group?: boolean }>>;
  getConversation?(id: Uint8Array): { readonly name?: string };
  // SDK member roster (ping-openmls-sdk >= 0.5.13 core / 0.6.13 JS).
  // Recovers each leaf's account id from the MLS credential — lets us set
  // a 1:1 row's `peerAccountId` locally even on a freshly-linked device
  // that never received the `ping.profile` re-send. Optional: absent on
  // older bindings (the call site guards on `typeof`).
  members?(id: Uint8Array): Promise<Array<{ user_id: Uint8Array; leaf_index: number }>>;
  // Catch-up sync (fetchSince per conversation). Used after a real-time join to
  // pull any message the inviter sent BEFORE our membership settled server-side
  // (skipped by live fanout but present in the event log) — the "first message
  // after invite is lost" race.
  syncConversations?(): Promise<unknown>;
}

// Bootstrap-sweep retry bounds. The sweep heals prior-session conversations on
// mount; it retries when the SDK's conversation map is still rehydrating (throw
// or empty list) so a transient not-ready state doesn't strand chats.
const BOOTSTRAP_SWEEP_MAX_ATTEMPTS = 4;
const BOOTSTRAP_SWEEP_BACKOFF_MS = 750;

// Grace window after a local leave during which a LIVE epoch update must NOT clear
// `leftAt`. Right after `markConversationLeft`, the SDK's own self-remove Commit
// hasn't landed yet, so straggler live events still arrive for the just-left
// conversation — the `com.ping.kick` echo, the reconcile's re-subscribe replaying
// the recent log, a trailing epoch bump — each of which re-enters `persistIfNew`
// and would falsely clear `leftAt`, reactivating the composer a second after the
// user left (the "composer comes back" bug). A genuine RE-INVITE always lands well
// after this window, so it still un-gates correctly. Mirrors
// `REJOIN_LEFT_CLEAR_GRACE_MS` in `apps/web/.../rejoinRecovery.ts`.
const LEFT_CLEAR_GRACE_MS = 2 * 60 * 1000;

// Debounced catch-up so a bulk join (device-linking sync) fires ONE sync, not
// one per conversation.
let catchUpTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleCatchUpSync(client: ClientWithConversationCallback): void {
  if (typeof client.syncConversations !== 'function') return;
  if (catchUpTimer !== null) return;
  catchUpTimer = setTimeout(() => {
    catchUpTimer = null;
    try {
      void client.syncConversations?.();
    } catch {
      /* best-effort */
    }
  }, 1500);
}

export interface UseIncomingConversationsOptions {
  readonly conversations: ConversationsPersister;
  readonly kv: KVStore;
  /** Optional notification sink. Fired once when a brand-new peer-initiated
   * conversation row is inserted (i.e. someone invited you / started a chat).
   * The platform decides whether to actually alert. */
  readonly onNotify?: (evt: IncomingNotification) => void;
  /** Fired with the conversation id every time a row is inserted OR upgraded
   * (group classification / peerAccountId backfill) by this hook. The host uses
   * it to hydrate the member roster from the directory IMMEDIATELY on join, so a
   * synced/invited conversation shows real names + avatars in the LIST without
   * waiting for the user to open it or for a fragile `ping.profile` re-send to
   * land. Best-effort; the handler must not throw. */
  readonly onConversationUpserted?: (conversationIdHex: string) => void;
}

export function useIncomingConversations(opts: UseIncomingConversationsOptions): void {
  const { client } = useSession();
  const { conversations, kv, onNotify, onConversationUpserted } = opts;

  useEffect(() => {
    if (client === null) return;

    const c = client as unknown as ClientWithConversationCallback;
    if (typeof c.onConversationUpdated !== 'function') {
      log.warn('incomingConversations.no_callback', {
        msg: "SDK exposes no onConversationUpdated — peer-initiated chats won't surface",
      });
      return;
    }

    const unsubscribe = c.onConversationUpdated((id, epoch, sender) => {
      const conversationIdHex = bytesToHex(id);
      // Fire-and-forget — the SDK doesn't await our handler. Errors
      // are logged but not surfaced; this is a UX nicety, not a
      // critical-path operation.
      void persistIfNew({
        conversationIdHex,
        conversationIdBytes: id,
        epoch,
        sender,
        conversations,
        kv,
        client: c,
        ...(onNotify !== undefined ? { onNotify } : {}),
        ...(onConversationUpserted !== undefined ? { onConversationUpserted } : {}),
      });
      // Diagnostic: dump the SDK's known-conversations list so we
      // can confirm whether the WASM-side HashMap actually contains
      // the id we just received. If it does, send should find it;
      // if not, the join succeeded only halfway (snapshot but no
      // in-memory insert) or the WASM state was wiped.
      if (typeof c.listConversations === 'function') {
        c.listConversations()
          .then((convs) => {
            const ids = (convs ?? []).map((m) => bytesToHex(m.id));
            log.info('incomingConversations.sdk_state', {
              eventConversationIdPrefix: conversationIdHex.slice(0, 8),
              sdkKnownConversations: ids.map((h) => h.slice(0, 8)),
              count: ids.length,
              matchesEventId: ids.includes(conversationIdHex),
            });
          })
          .catch((err) => {
            log.warn('incomingConversations.sdk_state.failed', {
              error: (err as Error).message,
            });
          });
      }
    });

    // Bootstrap reconciliation sweep. The live `onConversationUpdated` callback
    // above only fires for conversations that CHANGE during this session, so a
    // conversation whose Welcome was processed in a PRIOR session is never
    // re-evaluated on reload. That is fatal for a group that lost a first-Welcome
    // RACE — if the MLS GroupContext meta (`ping:meta:v1:`) wasn't yet readable
    // when the callback first fired, the row was persisted as a `direct` 1:1 and
    // would stay mis-classified forever (showing a member's name + no group
    // avatar). So on mount, replay `persistIfNew` for every conversation the SDK
    // already knows about: it is idempotent — existing rows hit the
    // backfill/upgrade branch (which upgrades a mis-classified direct→group from
    // the now-readable meta and refreshes the group avatar/roster) and a genuine
    // 1:1 stays direct. `onNotify` is intentionally OMITTED so the sweep never
    // raises invite notifications for already-known conversations.
    // Run the sweep with a BOUNDED RETRY. `listConversations` can throw or
    // return an empty set on mount when the SDK worker hasn't finished
    // rehydrating its in-memory group map (a hard reload races React against
    // `installSync`). A single silent no-op there leaves every prior-session
    // conversation missing until the NEXT reload — so we retry a few times with
    // backoff until the SDK yields a non-empty list (or we exhaust attempts).
    let sweepCancelled = false;
    const sweepTimers: Array<ReturnType<typeof setTimeout>> = [];
    const runBootstrapSweep = (attempt: number): void => {
      if (sweepCancelled || typeof c.listConversations !== 'function') return;
      void c
        .listConversations()
        .then((convs) => {
          if (sweepCancelled) return;
          const list = convs ?? [];
          for (const m of list) {
            // Skip the SDK's local-only device group — it is not a chat and must
            // never become a conversation row (persistIfNew also guards this).
            if (m.is_device_group === true || isDeviceGroupConversationId(bytesToHex(m.id))) {
              continue;
            }
            void persistIfNew({
              conversationIdHex: bytesToHex(m.id),
              conversationIdBytes: m.id,
              epoch: 0,
              sender: undefined,
              conversations,
              kv,
              client: c,
              // This is the reload/reconnect reconciliation sweep, NOT new
              // activity — it must never un-hide a chat the user deleted (that
              // made deleted chats reappear on every reload).
              fromReconcileSweep: true,
              ...(onConversationUpserted !== undefined ? { onConversationUpserted } : {}),
            });
          }
          // Empty list on an early attempt is suspicious (the SDK map may still
          // be rehydrating) — retry so we don't strand prior-session chats.
          const nonDeviceCount = list.filter(
            (m) => m.is_device_group !== true && !isDeviceGroupConversationId(bytesToHex(m.id)),
          ).length;
          if (nonDeviceCount === 0 && attempt < BOOTSTRAP_SWEEP_MAX_ATTEMPTS) {
            const t = setTimeout(
              () => runBootstrapSweep(attempt + 1),
              BOOTSTRAP_SWEEP_BACKOFF_MS * (attempt + 1),
            );
            sweepTimers.push(t);
          }
        })
        .catch((err) => {
          log.warn('incomingConversations.bootstrapSweep.failed', {
            error: (err as Error).message,
            attempt,
          });
          if (attempt < BOOTSTRAP_SWEEP_MAX_ATTEMPTS && !sweepCancelled) {
            const t = setTimeout(
              () => runBootstrapSweep(attempt + 1),
              BOOTSTRAP_SWEEP_BACKOFF_MS * (attempt + 1),
            );
            sweepTimers.push(t);
          }
        });
    };
    runBootstrapSweep(0);

    return () => {
      sweepCancelled = true;
      for (const t of sweepTimers) clearTimeout(t);
      try {
        unsubscribe();
      } catch (err) {
        log.warn('incomingConversations.unsubscribe.failed', {
          error: (err as Error).message,
        });
      }
    };
  }, [client, conversations, kv]);
}

interface PersistIfNewArgs {
  conversationIdHex: string;
  conversationIdBytes: Uint8Array;
  epoch: number;
  sender: Uint8Array | undefined;
  conversations: ConversationsPersister;
  kv: KVStore;
  client: ClientWithConversationCallback;
  onNotify?: (evt: IncomingNotification) => void;
  onConversationUpserted?: (conversationIdHex: string) => void;
  /** True when called from the reload/reconnect reconciliation sweep (not a live
   *  Welcome/epoch update). The sweep must NOT un-hide a user-deleted chat — that
   *  is what made deleted chats reappear on every reload. */
  fromReconcileSweep?: boolean;
}

// Grace period before a brand-new-conversation notification fires, so a
// call-group Welcome that beat its `call_invite` can be recognised + suppressed
// first (see the notify block in `persistIfNew`).
const CALL_GROUP_NOTIFY_GRACE_MS = 1500;

/** True when the SDK conversation is a per-call MLS group (name `call:<id>`). */
function isCallGroupConversation(
  client: ClientWithConversationCallback,
  conversationIdBytes: Uint8Array,
): boolean {
  if (typeof client.getConversation !== 'function') return false;
  try {
    const name = client.getConversation(conversationIdBytes).name;
    return typeof name === 'string' && name.startsWith(CALL_GROUP_NAME_PREFIX);
  } catch {
    return false;
  }
}

function readGroupPatchFromClient(
  client: ClientWithConversationCallback,
  conversationIdBytes: Uint8Array,
): (Partial<ConversationRecord> & { explicitTitle?: string }) | null {
  if (typeof client.getConversation !== 'function') return null;
  try {
    const conv = client.getConversation(conversationIdBytes);
    const metaName = conv.name;
    if (metaName === undefined || metaName.length === 0) return null;
    const patch = groupConversationPatchFromMeta(metaName, getCurrentUserAccountId());
    if (patch === null) return null;
    return {
      kind: patch.kind,
      name: patch.name,
      // The admin-set title (authoritative across devices), distinct from a
      // member-derived `name`. Lets the reconcile apply a rename over a stale
      // name even when the broadcast was missed.
      ...(patch.explicitTitle !== undefined ? { explicitTitle: patch.explicitTitle } : {}),
      memberAccountIds: patch.memberAccountIds,
      memberDisplayNames: patch.memberDisplayNames,
      ...(patch.avatarMediaId !== undefined ? { avatarMediaId: patch.avatarMediaId } : {}),
      ...(patch.ownerAccountId !== undefined ? { ownerAccountId: patch.ownerAccountId } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Resolve a group meta `avatarMediaId` (carried in GroupContext) to a download
 * URL and merge it into a conversation patch as `avatarUri`, so a joining device
 * renders the group avatar from group state. Best-effort: leaves the patch
 * untouched when there is no media id, no registered resolver, or the lookup
 * fails. Never overwrites an avatar the row already has.
 */
async function withResolvedGroupAvatar(
  patch: Partial<ConversationRecord>,
  existingAvatarUri: string | undefined,
): Promise<Partial<ConversationRecord>> {
  const mediaId = patch.avatarMediaId;
  if (mediaId === undefined || mediaId.length === 0) return patch;
  if (existingAvatarUri !== undefined && existingAvatarUri.length > 0) return patch;
  const url = await resolveMediaUrl(mediaId);
  return url !== undefined ? { ...patch, avatarUri: url } : patch;
}

/**
 * Resolve a 1:1 conversation's peer account id from the SDK member roster
 * (`client.members`). Recovers each leaf's account id locally from the MLS
 * credential, so a freshly-linked device can set `peerAccountId` even when
 * the `ping.profile` re-send never arrived. Returns the single non-self
 * account id, or `undefined` when the binding lacks `members()`, the call
 * fails, or the roster isn't an unambiguous 1:1 (so we never guess for a
 * group). `base64url(user_id) === peerAccountId` (same 32 bytes the BE
 * directory encodes).
 */
async function resolvePeerAccountIdViaRoster(
  client: ClientWithConversationCallback,
  conversationIdBytes: Uint8Array,
): Promise<string | undefined> {
  if (typeof client.members !== 'function') return undefined;
  try {
    const roster = await client.members(conversationIdBytes);
    const self = getCurrentUserAccountId();
    const peers = new Set<string>();
    for (const m of roster ?? []) {
      // GUARD: the MLS roster exposes each member's 32-byte `user_id`
      // (sha256(identity_pubkey)) — a DIFFERENT id space from the backend's
      // 16-byte directory `account_id`. Stamping the 32-byte UserId as
      // `peerAccountId` produces a value that never resolves to a profile and
      // that the peer-keyed dedup can never match → permanent "New chat" ghosts
      // that duplicate. Only accept a genuine 16-byte account id; otherwise the
      // caller falls back to the authoritative server roster
      // (`/v1/conversations/{id}/members`).
      if (m.user_id.length !== 16) continue;
      const acct = bytesToBase64url(m.user_id);
      if (acct.length > 0 && acct !== self) peers.add(acct);
    }
    return peers.size === 1 ? [...peers][0] : undefined;
  } catch {
    return undefined;
  }
}

/**
 * True if the SDK still holds this conversation's MLS group — i.e. the local user
 * is CURRENTLY a member. Deliberately id-AGNOSTIC: it compares CONVERSATION ids,
 * not member ids, so it can't false-negative on a roster whose self entry uses an
 * unexpected id space (the flaw of matching self in `client.members()`). MLS only
 * retains groups you belong to, so a group dropped by our own removal Commit is
 * absent here, while a re-joined group (re-invite) is present. The re-invite
 * detector pairs this with the epoch gate to tell a genuine re-add from our own
 * removal Commit. Best-effort → `false` on any error.
 */
async function sdkStillHasConversation(
  client: ClientWithConversationCallback,
  conversationIdHex: string,
): Promise<boolean> {
  if (typeof client.listConversations !== 'function') return false;
  try {
    const convs = await client.listConversations();
    const target = conversationIdHex.toLowerCase();
    return (convs ?? []).some((c) => bytesToHex(c.id).toLowerCase() === target);
  } catch {
    return false;
  }
}

// Per-conversation serialization. `persistIfNew` is invoked concurrently from
// the live `onConversationUpdated` callback, the bootstrap reconciliation sweep,
// and re-fired resumes. Two interleaved runs race the get→classify→put sequence:
// run A reads "no row" and decides {kind:'direct', name:'New chat'} (the MLS
// group meta wasn't readable yet), run B creates + UPGRADES the row to the group
// — then run A's stale `put` lands LAST and CLOBBERS the group back to a blank
// 'direct' row (which hydration then renames to the peer). Chaining per
// conversation id makes each run observe the previous run's writes, so the
// classification is never stale.
const persistInFlight = new Map<string, Promise<void>>();

function persistIfNew(args: PersistIfNewArgs): Promise<void> {
  const key = args.conversationIdHex;
  const prior = persistInFlight.get(key) ?? Promise.resolve();
  const next = prior
    .catch(() => undefined)
    .then(() => persistIfNewImpl(args))
    .catch((err) => {
      log.warn('incomingConversations.persist.failed', {
        conversationIdPrefix: key.slice(0, 8),
        error: (err as Error).message,
      });
    });
  persistInFlight.set(
    key,
    next.finally(() => {
      if (persistInFlight.get(key) === next) persistInFlight.delete(key);
    }),
  );
  return next;
}

async function persistIfNewImpl(args: PersistIfNewArgs): Promise<void> {
  const {
    conversationIdHex,
    conversationIdBytes,
    epoch,
    sender,
    conversations,
    kv,
    client,
    onNotify,
    onConversationUpserted,
    fromReconcileSweep,
  } = args;
  try {
    // Per-call MLS groups are ephemeral call infrastructure, not chats — never
    // materialize a conversation row for them. The creator's group carries the
    // `call:<id>` name, but the callee's join-via-Welcome stores NO name, so we
    // also consult the call-group registry (populated from `call_invite`'s
    // call_group_id). The call subsystem drives these groups directly.
    if (isRegisteredCallGroup(conversationIdHex) || isCallGroupConversation(client, conversationIdBytes)) {
      return;
    }

    // The SDK's local-only DEVICE GROUP (deterministic `ffdc…` id prefix) is
    // personal cross-device state, NOT a chat. It has no server-side membership,
    // so materializing a row for it produces a phantom "new chat" the user never
    // created (and every roster/avatar hydration 403s). Never persist it.
    if (isDeviceGroupConversationId(conversationIdHex)) {
      return;
    }

    const existing = await conversations.get(conversationIdHex);

    // Cache key used by mlsConversation.ts → getOrCreateMlsConversation.
    // Inviter writes one on first `client.createConversation()`; receiver
    // needs to write one too, otherwise the next time the chat opens
    // the helper falls through to createConversation and mints an
    // *orphan* MLS group where only the receiver is a member. Their
    // sends to that orphan group have no recipients on the BE side,
    // so the inviter never sees them.
    //
    // The mapping value mirrors the inviter's format: `{bytes: number[]}`
    // (IndexedDB doesn't structured-clone Uint8Arrays consistently
    // across backends). The key uses the same `mls.conv.<localConvId>`
    // prefix as the inviter — on the receiver, localConvId IS the
    // mlsIdHex, so the value points back at itself in bytes.
    await kv.put({
      key: `mls.conv.${conversationIdHex}`,
      value: { bytes: Array.from(conversationIdBytes) },
      updatedAt: Date.now(),
    });

    // Skip the invite-peer step on the receiver side — we joined via
    // Welcome, we aren't the inviter. Without this flag, every send
    // would re-enter invitePeerIfNeeded and try to fetchPeerDevices
    // (and 4xx, since localConv.peerAccountId is unset on receiver).
    await kv.put({
      key: `mls.invited.${conversationIdHex}`,
      value: { joinedViaWelcome: true, at: Date.now() },
      updatedAt: Date.now(),
    });

    if (existing) {
      // Un-hide a soft-deleted chat when a LIVE Welcome/epoch update arrives (a
      // genuine re-add / resume), so a resumed conversation comes back with its
      // preserved identity. But NEVER un-hide from the reload/reconnect
      // reconciliation SWEEP: the sweep replays `persistIfNew` for every
      // SDK-known conversation on mount, so un-hiding there resurrected every
      // chat the user had deleted on every reload ("deleted chats come back").
      // A new message (useIncomingMessages) still un-hides on real activity.
      if (fromReconcileSweep !== true && (existing.deletedAt ?? 0) > 0) {
        await conversations.update(conversationIdHex, { deletedAt: 0 });
      }
      // RE-INVITE after leaving — reactivate the composer, but ONLY on a genuine
      // re-add. This is EPOCH-gated, not time-gated: replayed old `Welcome` frames
      // arrive on EVERY reconnect (at any time), so a wall-clock grace can't tell
      // them from a real re-invite — but their MLS epoch can. We clear `leftAt`
      // only when the live update's epoch is STRICTLY GREATER than the epoch we left
      // at (`leftAtEpoch`) AND the SDK confirms we're a member again:
      //   • replayed old Welcome on reconnect → epoch <= leftAtEpoch → NOT cleared;
      //   • our own removal Commit → epoch advances but we're no longer a member →
      //     NOT cleared;
      //   • genuine re-add → higher epoch + we're a member → cleared.
      // Never from the reconcile SWEEP (a stale server roster right after we leave
      // must not un-leave us). When `leftAtEpoch` is absent (the leave broadcast
      // failed to send, so no epoch was captured) fall back to the wall-clock grace.
      const leftAt = existing.leftAt ?? 0;
      if (fromReconcileSweep !== true && leftAt > 0) {
        const leftAtEpoch = (existing as { leftAtEpoch?: number }).leftAtEpoch;
        const advancedPastLeave =
          leftAtEpoch !== undefined
            ? epoch > leftAtEpoch
            : Date.now() - leftAt >= LEFT_CLEAR_GRACE_MS;
        if (advancedPastLeave && (await sdkStillHasConversation(client, conversationIdHex))) {
          await conversations.update(conversationIdHex, { leftAt: 0, leftAtEpoch: 0 });
        }
      }
      if (existing.kind !== 'group') {
        const upgradeRaw = readGroupPatchFromClient(client, conversationIdBytes);
        const upgrade =
          upgradeRaw !== null
            ? await withResolvedGroupAvatar(upgradeRaw, existing.avatarUri)
            : null;
        if (upgrade !== null) {
          await conversations.update(conversationIdHex, {
            ...upgrade,
            // DURABLE group marker: once the MLS meta classifies this row as a
            // group it can NEVER be collapsed into the same-peer 1:1 again, even
            // if a later race briefly reverts `kind` (see `isRealGroup`).
            explicitGroup: true,
            // C: a GROUP must NOT retain a 1:1 `peerAccountId`. While the row was
            // momentarily classified 'direct' (single-peer groups are server-side
            // DMs), `hydrateMemberProfiles` may have stamped the sole other member
            // as the peer. The authoritative classifier treats `peerAccountId != nil`
            // as "this is a DM", which would re-render the just-upgraded group as a
            // 1:1. Clear it on upgrade so a single-peer group renders as a group.
            ...(existing.peerAccountId !== undefined && existing.peerAccountId.length > 0
              ? { peerAccountId: '' }
              : {}),
          });
          log.info('incomingConversations.upgrade_group', {
            conversationIdPrefix: conversationIdHex.slice(0, 8),
            clearedPeer: existing.peerAccountId !== undefined && existing.peerAccountId.length > 0,
          });
          // Newly-classified group → resolve its roster names/avatars now.
          onConversationUpserted?.(conversationIdHex);
        } else if (existing.peerAccountId === undefined || existing.peerAccountId.length === 0) {
          // Backfill a 1:1's peerAccountId from the MLS roster if it's still
          // missing (e.g. the row was created before the re-send arrived).
          const peer = await resolvePeerAccountIdViaRoster(client, conversationIdBytes);
          if (peer !== undefined) {
            await conversations.update(conversationIdHex, { peerAccountId: peer });
            // peerAccountId just became known → hydrate the name/avatar now.
            onConversationUpserted?.(conversationIdHex);
          }
        }
      } else {
        // Existing GROUP row: backfill from the authenticated MLS group meta,
        // which may only have become readable AFTER the row was first created
        // (e.g. a bare/early row inserted from the conversations bootstrap, or a
        // group created on iOS whose Welcome materialised the row on web before
        // its GroupContext meta resolved). Without this, an iOS-created group is
        // stuck on web showing header initials + raw account ids instead of the
        // real avatar + member display names. We MERGE — never lose data the row
        // already has, only fill in what's missing or has newly grown.
        const metaRaw = readGroupPatchFromClient(client, conversationIdBytes);
        if (metaRaw !== null) {
          const update: Partial<ConversationRecord> = {};
          if (metaRaw.memberDisplayNames !== undefined) {
            const merged = { ...(existing.memberDisplayNames ?? {}), ...metaRaw.memberDisplayNames };
            // Only write if the merge actually adds/changes a name, so we don't
            // churn the row (and re-trigger renders) on every redundant Welcome.
            const existingNames = existing.memberDisplayNames ?? {};
            const grew =
              Object.keys(merged).length !== Object.keys(existingNames).length ||
              Object.entries(merged).some(([k, v]) => existingNames[k] !== v);
            if (grew) update.memberDisplayNames = merged;
          }
          if (metaRaw.memberAccountIds !== undefined) {
            const union = [...new Set([...(existing.memberAccountIds ?? []), ...metaRaw.memberAccountIds])].sort();
            if (union.length !== (existing.memberAccountIds ?? []).length) {
              update.memberAccountIds = union;
            }
          }
          // AUTHORITATIVE title: the MLS meta carries the admin-set title,
          // rewritten into the GroupContext on every rename. When an explicit
          // title is present it is the source of truth for EVERY member — apply
          // it even OVER a non-empty stale name, so a peer that missed the
          // best-effort `com.ping.group_name` broadcast still converges to the
          // admin's rename from group state alone. (A group with no explicit
          // title falls back to fill-when-empty below so we never clobber a name
          // with an auto-derived member list.)
          if (metaRaw.explicitTitle !== undefined && metaRaw.explicitTitle.length > 0) {
            if (existing.name !== metaRaw.explicitTitle) update.name = metaRaw.explicitTitle;
          } else if (
            (existing.name === undefined || existing.name.length === 0) &&
            metaRaw.name !== undefined &&
            metaRaw.name.length > 0
          ) {
            update.name = metaRaw.name;
          }
          // AUTHORITATIVE avatar: the meta carries the admin-set avatarMediaId.
          // When it CHANGES, re-resolve a fresh URL and overwrite — so an avatar
          // change reaches every member from group state, not just the
          // best-effort `com.ping.group_avatar` broadcast. When it's unchanged
          // but the row never resolved a URL (or it expired), backfill one.
          if (
            metaRaw.avatarMediaId !== undefined &&
            metaRaw.avatarMediaId.length > 0 &&
            metaRaw.avatarMediaId !== existing.avatarMediaId
          ) {
            update.avatarMediaId = metaRaw.avatarMediaId;
            const url = withAvatarCacheKey(
              await resolveMediaUrl(metaRaw.avatarMediaId),
              metaRaw.avatarMediaId,
            );
            if (url !== undefined && url.length > 0) update.avatarUri = url;
          } else if (
            (existing.avatarUri === undefined || existing.avatarUri.length === 0) &&
            metaRaw.avatarMediaId !== undefined &&
            metaRaw.avatarMediaId.length > 0
          ) {
            const url = withAvatarCacheKey(
              await resolveMediaUrl(metaRaw.avatarMediaId),
              metaRaw.avatarMediaId,
            );
            if (url !== undefined && url.length > 0) {
              update.avatarUri = url;
              if (existing.avatarMediaId === undefined || existing.avatarMediaId.length === 0) {
                update.avatarMediaId = metaRaw.avatarMediaId;
              }
            }
          }
          if (metaRaw.ownerAccountId !== undefined && (existing.ownerAccountId === undefined || existing.ownerAccountId.length === 0)) {
            update.ownerAccountId = metaRaw.ownerAccountId;
          }
          if (Object.keys(update).length > 0) {
            await conversations.update(conversationIdHex, update);
            log.info('incomingConversations.group_backfill', {
              conversationIdPrefix: conversationIdHex.slice(0, 8),
              keys: Object.keys(update).join(','),
            });
            // Names/avatar just improved → re-hydrate the roster view.
            onConversationUpserted?.(conversationIdHex);
          }
        }
      }
      return;
    }

    const groupPatchRaw = readGroupPatchFromClient(client, conversationIdBytes);
    const groupPatch =
      groupPatchRaw !== null
        ? await withResolvedGroupAvatar(groupPatchRaw, undefined)
        : null;
    const isGroup = groupPatch !== null;

    // For a 1:1, resolve the peer's account id from the MLS member roster so
    // the row carries a reliable `peerAccountId` even on a freshly-linked
    // device that never received the `ping.profile` re-send. The display
    // NAME still comes from the re-send / direct profile. No-op for groups
    // and on bindings without `members()`.
    const peerAccountId = isGroup
      ? undefined
      : await resolvePeerAccountIdViaRoster(client, conversationIdBytes);

    // Re-check the call-group registry: suppressCallGroup (called by the call
    // layer when the call_invite arrives) may have registered and hard-purged
    // this id between our initial check and now. If we write a row here, it
    // sticks past the purge — so bail out if the id is now registered.
    if (isRegisteredCallGroup(conversationIdHex)) return;

    // Final anti-clobber guard. Even with per-conversation serialization of
    // `persistIfNew`, a DIFFERENT hook (the message / `com.ping.group_name`
    // handler) may have created — and possibly already classified as a GROUP —
    // this row while we were resolving the meta/roster above. A blind `put` is a
    // full REPLACE that would wipe that row's group kind/title/avatar back to a
    // fresh 'direct'/'New chat'. So NEVER put over an existing row: upgrade it in
    // place when we have a group classification it lacks, otherwise leave it for
    // the existing-row path. This is the root fix for "group flips to peer name".
    const raced = await conversations.get(conversationIdHex);
    if (raced !== undefined) {
      if (isGroup && raced.kind !== 'group' && groupPatch !== null) {
        await conversations.update(conversationIdHex, {
          kind: 'group',
          // DURABLE group marker — see `isRealGroup`. Prevents this row (a
          // single-peer group looks roster-identical to the 1:1) from ever being
          // collapsed into the same-peer DM.
          explicitGroup: true,
          ...(groupPatch.name !== undefined && groupPatch.name.length > 0
            ? { name: groupPatch.name }
            : {}),
          ...(groupPatch.memberAccountIds !== undefined
            ? { memberAccountIds: groupPatch.memberAccountIds }
            : {}),
          ...(groupPatch.memberDisplayNames !== undefined
            ? { memberDisplayNames: groupPatch.memberDisplayNames }
            : {}),
          ...(groupPatch.avatarUri !== undefined ? { avatarUri: groupPatch.avatarUri } : {}),
          ...(groupPatch.avatarMediaId !== undefined
            ? { avatarMediaId: groupPatch.avatarMediaId }
            : {}),
          ...(groupPatch.ownerAccountId !== undefined
            ? { ownerAccountId: groupPatch.ownerAccountId }
            : {}),
          // C: clear any 1:1 peerAccountId a concurrent hydrate stamped on the
          // row — a group must not carry a peer (else it re-renders as a DM).
          ...(raced.peerAccountId !== undefined && raced.peerAccountId.length > 0
            ? { peerAccountId: '' }
            : {}),
        });
      }
      log.info('incomingConversations.persist.skipClobber', {
        conversationIdPrefix: conversationIdHex.slice(0, 8),
        existingKind: raced.kind,
        isGroup,
      });
      onConversationUpserted?.(conversationIdHex);
      return;
    }

    log.info('incomingConversations.persist', {
      conversationIdPrefix: conversationIdHex.slice(0, 8),
      epoch,
      senderPrefix: sender ? bytesToHex(sender).slice(0, 8) : null,
      isGroup,
      peerAccountIdResolved: peerAccountId !== undefined,
    });

    await conversations.put({
      id: conversationIdHex,
      kind: isGroup ? 'group' : 'direct',
      name: isGroup ? (groupPatch!.name ?? 'Group chat') : 'New chat',
      createdAt: Date.now(),
      joinedViaInvite: true,
      // DURABLE group marker at first materialization: a received group (incl. a
      // single-peer group, which is roster-identical to a 1:1) is stamped so the
      // direct-dedup passes can never merge it into the same-peer DM. Only set
      // when we positively classified it as a group from the MLS meta.
      ...(isGroup ? { explicitGroup: true } : {}),
      ...(peerAccountId !== undefined ? { peerAccountId } : {}),
      ...(isGroup && groupPatch!.memberAccountIds !== undefined
        ? { memberAccountIds: groupPatch!.memberAccountIds }
        : {}),
      ...(isGroup && groupPatch!.memberDisplayNames !== undefined
        ? { memberDisplayNames: groupPatch!.memberDisplayNames }
        : {}),
      ...(isGroup && groupPatch!.avatarUri !== undefined
        ? { avatarUri: groupPatch!.avatarUri }
        : {}),
      ...(isGroup && groupPatch!.avatarMediaId !== undefined
        ? { avatarMediaId: groupPatch!.avatarMediaId }
        : {}),
      // C3: seed the authenticated owner (creator) so role authority is
      // bootstrapped on this member device.
      ...(isGroup && groupPatch!.ownerAccountId !== undefined
        ? { ownerAccountId: groupPatch!.ownerAccountId }
        : {}),
    });

    // Hydrate the roster from the directory NOW (names + avatars) so the LIST
    // shows real identities immediately on join — not "New chat" until the user
    // opens it or a `ping.profile` re-send happens to land.
    onConversationUpserted?.(conversationIdHex);

    // Catch-up: pull any message sent before our membership settled server-side
    // (the "first message after invite is lost" race). fetchSince from cursor 0
    // returns the full event log; already-seen messages dedupe on insert.
    scheduleCatchUpSync(client);

    // Surface a transient "Receiving N chats…" banner in the
    // chat-list panel while Welcomes are flowing in. The store
    // auto-decays after `RECENT_JOIN_WINDOW_MS` of inactivity, so we
    // never need to signal "done" from here.
    useChatSyncStore.getState().noteJoin(conversationIdHex);

    // Notify: a brand-new peer-initiated conversation = "someone started a
    // chat / added you". Only fires on first insert (this branch). The
    // platform gates it on focus/prefs.
    //
    // DEFER + RE-CHECK: a per-call MLS group's Welcome can land JUST BEFORE the
    // `call_invite` that registers it as a call group (`suppressCallGroup`).
    // Firing immediately would surface a spurious "someone started a
    // conversation" for a call, and the ghost row is purged the moment the
    // invite lands. Waiting one beat lets the invite register/purge the group;
    // if it does, we suppress the notification. A genuine new chat just notifies
    // ~1s later, which is imperceptible.
    if (onNotify !== undefined) {
      const name = isGroup ? groupPatch!.name ?? 'Group chat' : undefined;
      const payload = {
        kind: 'invite' as const,
        conversationId: conversationIdHex,
        isMention: false,
        sentAt: Date.now(),
        ...(name !== undefined ? { conversationName: name } : {}),
        ...(sender !== undefined ? { senderId: bytesToHex(sender) } : {}),
      };
      setTimeout(() => {
        void (async () => {
          // Recognised as a call group in the grace window → never notify.
          if (isRegisteredCallGroup(conversationIdHex)) return;
          // Row was purged (call-group cleanup) or hidden → nothing to announce.
          const still = await conversations.get(conversationIdHex).catch(() => undefined);
          if (still === undefined || (still.deletedAt ?? 0) > 0) return;
          try {
            onNotify(payload);
          } catch (err) {
            log.warn('incomingConversations.notify.failed', {
              error: (err as Error).message,
            });
          }
        })();
      }, CALL_GROUP_NOTIFY_GRACE_MS);
    }
  } catch (err) {
    log.error('incomingConversations.persist.failed', err as Error, {
      conversationIdPrefix: conversationIdHex.slice(0, 8),
    });
  }
}

function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += (b[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}
